/* Necessary Imports */
import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { 
  LTSegmentedControl,
  LTSwitch 
} from '@lambdatestincprivate/lt-components';

/* Component Imports */
import { useQueryParams } from '../../hooks/useQueryParams';
import useTestUrlRedirection from '../../hooks/useTestUrlRedirection';

import {
  getUrlObject,
  sendAmplitudeEvents,
} from '../../utils/utilityFunctions';
import { TEST_DETAIL_ID_PREFIX } from '../../constants/View';
import { EventsMap } from '../../constants/events';

import BasicInfo from './Tabs/BasicInfo';
import InputConfig from './Tabs/InputConfig';
import Media from './Tabs/Media';

import styles from './index.module.css';

const NavItems = {
  BASIC_INFO: 'Basic Info',
  INPUT_CONFIG: 'Input Config',
  MEDIA: 'Media',
};

const NavList = [
  {
    contentId: 'meta-data-basic-info',
    name: 'basic_info',
    displayName: NavItems.BASIC_INFO,
    label: NavItems.BASIC_INFO,
    value: NavItems.BASIC_INFO,
  },
  {
    contentId: 'meta-data-input-config',
    name: 'input_config',
    displayName: NavItems.INPUT_CONFIG,
    label: NavItems.INPUT_CONFIG,
    value: NavItems.INPUT_CONFIG,
  },
  {
    contentId: 'meta-data-media',
    name: 'media',
    displayName: NavItems.MEDIA,
    label: NavItems.MEDIA,
    value: NavItems.MEDIA,
  },
];

const MetaData = ({
  automationMediaHost,
  testDetails,
  downloadAsset,
  filesToRender,
  onChange,
}) => {
  const [inputConfigShowJson, setInputConfigShowJson] = useState(false);
  const navigate = useNavigate();
  const searchParams = useQueryParams();
  const selectedTabSubType =
    searchParams.get('selectedTabSubType') || 'basic_info';

  const [selectedNavItem, setSelectedNavItem] = useTestUrlRedirection({
    type: selectedTabSubType,
    navListItems: NavList,
    urlParam: 'selectedTabSubType',
    navigate,
  });

  const currentTabId = selectedNavItem
    ?.toLowerCase()
    .split(' ')
    .join('-');

  const currentTabName = selectedNavItem
    ?.toLowerCase()
    .split(' ')
    .join('_');

  const handleChange = (selectedIndex) => {
    const selectedItem = NavItemsToRender[selectedIndex];
    setSelectedNavItem(selectedItem?.value);

    sendAmplitudeEvents(EventsMap.TEST_VIEW_SELECT_LOGS, {
      selectedTabSubType: selectedItem?.value,
    });

    const url = getUrlObject();
    // update the url and reducer whenever side tab is changed
    url.searchParams.set('selectedTabSubType', selectedItem?.name);
    navigate(`${url.pathname}${url.search}`);

    onChange(selectedItem);
  };

  // Remove Media tab from on-prem.
  const { mode = '' } = testDetails;
  const NavItemsToRender = [...NavList];
  if (automationMediaHost && mode === 'hypertest') {
    NavItemsToRender.pop();
  }

  return (
    <div
      aria-labelledby="test-detail-meta-data-tab"
      className={styles.container}
      id="test-detail-meta-data"
      role="tabpanel"
    >
      <div className={styles.topBar} id="metadata-side-nav">
        <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
          <LTSegmentedControl
            aria-label="Meta Data"
            className={styles.logsSegmentedControl}
            data-testid="metadata-tab-control"
            onChange={handleChange}
            options={NavItemsToRender}
            selectedValue={selectedNavItem}
            buttonStyles={{
              whiteSpace: "nowrap",
              height: "fit-content",
            }}
          />
          {selectedNavItem === NavItems.INPUT_CONFIG && (
            <LTSwitch
              data-testid="input-config-json-toggle"
              defaultState={!inputConfigShowJson}
              id="lt-input-config-switch"
              label="Show JSON"
              labelId="show-json-toggle"
              onValueChange={value => {
                setInputConfigShowJson(!value);
              }}
              switchStyle={{
                whiteSpace: "nowrap",
              }}
            />
          )}
        </div>
      </div>
      <div
        aria-labelledby={`${TEST_DETAIL_ID_PREFIX}-metadata-${currentTabName}`}
        className={styles.content}
        id={`meta-data-${currentTabId}`}
        role="tabpanel"
      >
        {selectedNavItem === NavItems.MEDIA && (
          <Media
            testDetails={testDetails}
            filesToRender={filesToRender}
            downloadAsset={downloadAsset}
          />
        )}
        {selectedNavItem === NavItems.BASIC_INFO && (
          <BasicInfo testDetails={testDetails} />
        )}
        {selectedNavItem === NavItems.INPUT_CONFIG && (
          <InputConfig testDetails={testDetails} hideJson={inputConfigShowJson}/>
        )}
      </div>
    </div>
  );
};

export default MetaData;
